import { createFileRoute } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";
import { useLingui } from "@lingui/react";
import { useAuth } from "@/hooks/useAuth";
import { companyApi } from "@/lib/api/services/company.api";
import { locationApi } from "@/lib/api/services/location.api";
import { userApi } from "@/lib/api/services/user.api";
import { materialApi } from "@/lib/api/services/material.api";
import { recipeApi } from "@/lib/api/services/recipe.api";
import { menuApi } from "@/lib/api/services/menu.api";
import { organizationApi } from "@/lib/api/services/organization.api";
import { AdminPageHeader } from "@/components/admin/common/AdminPageHeader";
import { hasWidgetDataKey, resolveDashboardWidgets } from "@/features/dashboard/layout";
import { resolveDashboardRoleBucket } from "@/features/dashboard/roles";
import { renderDashboardWidget } from "@/features/dashboard/widget-registry";
import { BusinessDashboardSkeleton, type BusinessWidgetData } from "@/features/dashboard/widgets/business-widgets";
import type { DashboardRoleBucket, DashboardWidgetConfig, DashboardWidgetId } from "@/features/dashboard/types";

export const Route = createFileRoute("/(protected)/_layout/dashboard")({
  component: DashboardPage,
});

function hasWidget(widgets: DashboardWidgetConfig[], id: DashboardWidgetId) {
  return widgets.some((widget) => widget.id === id);
}

function isBusinessBucket(bucket: DashboardRoleBucket) {
  return bucket === "SUPPORT" || bucket === "ORG_ADMIN";
}

function DashboardPage() {
  const { i18n } = useLingui();
  const { isLoading, hasRole, hasPermission, isAuthenticated } = useAuth();

  const roleBucket = resolveDashboardRoleBucket({ hasRole, hasPermission });
  const widgets = resolveDashboardWidgets(roleBucket, { hasRole, hasPermission });

  const showBusinessStats = hasWidget(widgets, "BUSINESS_STATS");
  const canReadCompanyStats = hasPermission("company:read");
  const canUseAdminScope = hasPermission("*:*");
  const isBusinessDashboard = isBusinessBucket(roleBucket);
  const isSuperAdminDashboard = roleBucket === "SUPPORT";
  const needsBusinessSummary = hasWidgetDataKey(widgets, "BUSINESS_SUMMARY");

  const { data: companiesPage, isLoading: companiesLoading } = useQuery({
    queryKey: ["companies", "dashboard", "page1"],
    queryFn: () => companyApi.paginate({ limit: 1 }),
    enabled: isAuthenticated && isBusinessDashboard && canReadCompanyStats,
  });

  const primaryCompany = companiesPage?.data[0];

  const { data: organizationsStats, isLoading: organizationsStatsLoading } = useQuery({
    queryKey: ["organizations-stats", "dashboard"],
    queryFn: () => organizationApi.getGlobalStats({ adminScope: true }),
    enabled:
      isAuthenticated &&
      isBusinessDashboard &&
      isSuperAdminDashboard &&
      canUseAdminScope &&
      canReadCompanyStats &&
      needsBusinessSummary,
  });

  const [
    companiesStatsQuery,
    locationsStatsQuery,
    usersStatsQuery,
    materialsStatsQuery,
    recipesStatsQuery,
    menusStatsQuery,
  ] = useQueries({
    queries: [
      {
        queryKey: ["companies-stats", "dashboard"],
        queryFn: () => companyApi.getGlobalStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
      {
        queryKey: ["locations-stats", "dashboard"],
        queryFn: () => locationApi.getStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
      {
        queryKey: ["users-stats", "dashboard"],
        queryFn: () => userApi.getStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
      {
        queryKey: ["materials-stats", "dashboard"],
        queryFn: () => materialApi.getStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
      {
        queryKey: ["recipes-stats", "dashboard"],
        queryFn: () => recipeApi.getStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
      {
        queryKey: ["menus-stats", "dashboard"],
        queryFn: () => menuApi.getStats(),
        enabled:
          isAuthenticated &&
          isBusinessDashboard &&
          canReadCompanyStats &&
          needsBusinessSummary,
      },
    ],
  });

  const statsLoading =
    organizationsStatsLoading ||
    companiesStatsQuery.isLoading ||
    locationsStatsQuery.isLoading ||
    usersStatsQuery.isLoading ||
    materialsStatsQuery.isLoading ||
    recipesStatsQuery.isLoading ||
    menusStatsQuery.isLoading;

  if (isLoading) {
    return <BusinessDashboardSkeleton />;
  }

  if (widgets.length === 0) {
    return (
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-3">
        <AdminPageHeader
          icon={TrendingUp}
          title={i18n._("Dashboard")}
          description={i18n._(
            "No dashboard widgets are available for your current role and permissions.",
          )}
        />
      </div>
    );
  }

  if (isBusinessDashboard && canReadCompanyStats && (companiesLoading || (primaryCompany && statsLoading))) {
    return <BusinessDashboardSkeleton />;
  }

  const businessData: BusinessWidgetData | undefined = isBusinessDashboard
    ? {
        isInternalDashboard: roleBucket === "SUPPORT",
        organizationsStats: organizationsStats
          ? { totalOrganizations: organizationsStats.total }
          : undefined,
        companiesStats: companiesStatsQuery.data
          ? { totalCompanies: companiesStatsQuery.data.total }
          : undefined,
        locationsStats: locationsStatsQuery.data,
        usersStats: usersStatsQuery.data,
        materialsStats: materialsStatsQuery.data,
        recipesStats: recipesStatsQuery.data,
        menusStats: menusStatsQuery.data,
        statsLoading,
      }
    : undefined;

  if (!isBusinessDashboard) {
    return (
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-3">
        {widgets.map((widget) => (
          <div key={widget.id}>{renderDashboardWidget(widget.id, {})}</div>
        ))}
      </div>
    );
  }

  if (!canReadCompanyStats) {
    return (
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-3">
        <AdminPageHeader
          icon={TrendingUp}
          title={i18n._("Dashboard")}
          description={i18n._("Your dashboard widgets are loading based on your role permissions.")}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-3">
      <AdminPageHeader
        icon={TrendingUp}
        title={i18n._("Dashboard")}
        description={
          roleBucket === "SUPPORT"
            ? i18n._("System-wide overview for internal and support teams")
            : i18n._("Organization-level overview for your operational teams")
        }
      />

      {primaryCompany ? (
        <p className="-mt-4 px-3 text-sm text-(--color-text-secondary)">
          {i18n._("Viewing: {companyName}", { companyName: primaryCompany.name })}
        </p>
      ) : null}

      {showBusinessStats ? renderDashboardWidget("BUSINESS_STATS", { businessData }) : null}

      {(hasWidget(widgets, "BUSINESS_QUICK_ACTIONS") || hasWidget(widgets, "BUSINESS_SYSTEM_OVERVIEW")) ? (
        <div className="grid gap-4 md:grid-cols-2">
          {hasWidget(widgets, "BUSINESS_QUICK_ACTIONS")
            ? renderDashboardWidget("BUSINESS_QUICK_ACTIONS", { businessData })
            : null}
          {hasWidget(widgets, "BUSINESS_SYSTEM_OVERVIEW")
            ? renderDashboardWidget("BUSINESS_SYSTEM_OVERVIEW", { businessData })
            : null}
        </div>
      ) : null}
    </div>
  );
}
